import React from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Button,
  Chip,
  Select,
  MenuItem,
  InputLabel,
  FormGroup,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import { 
  Payment, 
  Gavel, 
  AccountBalance, 
  CheckCircle,
  Shield,
  Verified,
  MonetizationOn
} from '@mui/icons-material';

const PaymentLegalStep = ({ formData, updateFormData, errors }) => {
  const handleChange = (field) => (event) => {
    const value = event.target.value;
    updateFormData({ [field]: value });
  };

  const handleCheckboxChange = (field) => (event) => {
    const value = event.target.checked;
    updateFormData({ [field]: value });
  };

  const paymentMethods = [
    { 
      value: 'direct_deposit', 
      label: 'Direct Deposit (ACH)', 
      description: 'Fastest and most secure - funds available next business day',
      recommended: true
    },
    { 
      value: 'paypal', 
      label: 'PayPal', 
      description: 'Quick transfers - small processing fee may apply',
      recommended: false
    }
  ];

  const taxClassifications = [
    { 
      value: '1099_contractor', 
      label: '1099 Independent Contractor', 
      description: 'Self-employed contractor responsible for own taxes',
      recommended: true,
      selected: true
    }
  ];

  const requiredAgreements = [
    {
      id: 'termsOfService',
      title: 'Terms of Service Agreement',
      description: 'General terms governing the working relationship',
      required: true
    },
    {
      id: 'privacyPolicy',
      title: 'Privacy Policy Agreement',
      description: 'How we handle and protect your personal information',
      required: true
    },
    {
      id: 'safetyGuidelines',
      title: 'Safety Guidelines and Protocols',
      description: 'Safety procedures and emergency protocols for cleaning jobs',
      required: true
    },
    {
      id: 'qualityStandards',
      title: 'Quality Standards Agreement',
      description: 'Service quality expectations and performance standards',
      required: true
    },
    {
      id: 'backgroundCheckConsent',
      title: 'Background Check Consent',
      description: 'Authorization for background verification process',
      required: true
    }
  ];

  const optionalAgreements = [

  ];

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Payment & Legal Agreements
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Set up your payment preferences and review the required legal agreements to complete your application.
      </Typography>

      {/* Payment Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Payment color="primary" /> Payment Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          {/* Payment Method Selection */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
            Preferred Payment Method
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Choose how you'd like to receive your earnings. You can change this later in your profile.
          </Typography>

          <FormControl error={!!errors.paymentMethod} required sx={{ width: '100%' }}>
            <RadioGroup
              value={formData.paymentMethod || ''}
              onChange={handleChange('paymentMethod')}
            >
              {paymentMethods.map((method) => (
                <Paper 
                  key={method.value}
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    border: formData.paymentMethod === method.value ? 2 : 1,
                    borderColor: formData.paymentMethod === method.value ? 'primary.main' : 'grey.300'
                  }}
                >
                  <FormControlLabel
                    value={method.value}
                    control={<Radio />}
                    label={
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {method.label}
                          </Typography>
                          {method.recommended && (
                            <Chip 
                              label="Recommended" 
                              color="primary" 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                          {method.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', width: '100%' }}
                  />
                </Paper>
              ))}
            </RadioGroup>
            {errors.paymentMethod && (
              <Typography color="error" variant="caption">
                {errors.paymentMethod}
              </Typography>
            )}
          </FormControl>

          {/* Bank Account Information (shown if direct deposit selected) */}
          {formData.paymentMethod === 'direct_deposit' && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <AccountBalance sx={{ verticalAlign: 'middle', mr: 1 }} />
                  <strong>Direct Deposit Setup:</strong> You'll receive secure bank account setup instructions 
                  after your application is approved. We use bank-level encryption to protect your information.
                </Typography>
              </Alert>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Bank Name"
                    placeholder="e.g., Chase, Bank of America, Wells Fargo"
                    value={formData.bankName || ''}
                    onChange={handleChange('bankName')}
                    helperText="Name of your bank or credit union"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Account Type</InputLabel>
                    <Select
                      value={formData.accountType || ''}
                      onChange={handleChange('accountType')}
                      label="Account Type"
                    >
                      <MenuItem value="checking">Checking Account</MenuItem>
                      <MenuItem value="savings">Savings Account</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tax Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MonetizationOn color="primary" /> Tax Classification
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Your tax classification affects how you're paid and what tax documents you'll receive. 
              Consult a tax professional if you're unsure which option is right for you.
            </Typography>
          </Alert>

          <FormControl error={!!errors.taxClassification} required sx={{ width: '100%' }}>
            <FormLabel sx={{ fontWeight: 'bold', mb: 2 }}>
              Preferred Tax Classification
            </FormLabel>
            <RadioGroup
              value={formData.taxClassification || ''}
              onChange={handleChange('taxClassification')}
            >
              {taxClassifications.map((classification) => (
                <Paper 
                  key={classification.value}
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    mb: 2, 
                    border: formData.taxClassification === classification.value ? 2 : 1,
                    borderColor: formData.taxClassification === classification.value ? 'primary.main' : 'grey.300'
                  }}
                >
                  <FormControlLabel
                    value={classification.value}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                          {classification.label}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {classification.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start' }}
                  />
                </Paper>
              ))}
            </RadioGroup>
            {errors.taxClassification && (
              <Typography color="error" variant="caption">
                {errors.taxClassification}
              </Typography>
            )}
          </FormControl>
        </CardContent>
      </Card>

      {/* Legal Agreements */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Gavel color="primary" /> Legal Agreements
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <Shield sx={{ verticalAlign: 'middle', mr: 1 }} />
              Please review and accept the following agreements. These protect both you and our clients, 
              and ensure everyone understands their rights and responsibilities.
            </Typography>
          </Alert>

          {/* Required Agreements */}
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
            Required Agreements
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            All of these agreements must be accepted to complete your application.
          </Typography>

          <List>
            {requiredAgreements.map((agreement) => (
              <ListItem key={agreement.id} sx={{ px: 0 }}>
                <ListItemIcon>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[agreement.id] || false}
                        onChange={handleCheckboxChange(agreement.id)}
                        color="primary"
                        required
                      />
                    }
                    label=""
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {agreement.title}
                      </Typography>
                      <Chip label="Required" color="error" size="small" variant="outlined" />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {agreement.description}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          const documentName = agreement.id === 'termsOfService' ? 'terms-of-service' : 
                                              agreement.id === 'privacyPolicy' ? 'privacy-policy' :
                                              agreement.id === 'safetyGuidelines' ? 'safety-guidelines' :
                                              agreement.id === 'qualityStandards' ? 'quality-standards' :
                                              agreement.id === 'backgroundCheckConsent' ? 'background-check-consent' :
                                              'document';
                          window.open(`${window.location.origin}/documents/${documentName}.html`, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{ 
                          fontSize: '0.875rem', 
                          textTransform: 'none',
                          color: 'primary.main',
                          p: 0,
                          minWidth: 'auto'
                        }}
                        aria-label={`View ${agreement.title} document in new tab`}
                      >
                        View Full Document →
                      </Button>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>



          <List>
            {optionalAgreements.map((agreement) => (
              <ListItem key={agreement.id} sx={{ px: 0 }}>
                <ListItemIcon>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[agreement.id] || false}
                        onChange={handleCheckboxChange(agreement.id)}
                        color="primary"
                      />
                    }
                    label=""
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {agreement.title}
                      </Typography>
                      <Chip label="Optional" color="info" size="small" variant="outlined" />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                        {agreement.description}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          const documentName = agreement.id === 'referralProgram' ? 'referral-program' : 'marketing';
                          window.open(`${window.location.origin}/documents/${documentName}.html`, '_blank', 'noopener,noreferrer');
                        }}
                        sx={{ 
                          fontSize: '0.875rem', 
                          textTransform: 'none',
                          color: 'primary.main',
                          p: 0,
                          minWidth: 'auto'
                        }}
                        aria-label={`View ${agreement.title} information in new tab`}
                      >
                        Learn More →
                      </Button>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>

      {/* Final Acknowledgment */}
      <Card sx={{ mb: 3, backgroundColor: 'primary.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Verified color="primary" /> Final Acknowledgment
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.finalAcknowledgment || false}
                onChange={handleCheckboxChange('finalAcknowledgment')}
                color="primary"
                required
              />
            }
            label={
              <Typography variant="body1">
                <strong>I acknowledge that:</strong> I have read and understood all agreements, 
                provided accurate information in this application, and agree to maintain professional 
                standards while representing 5 Fairies Cleaning.
              </Typography>
            }
          />
          
          {errors.finalAcknowledgment && (
            <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
              {errors.finalAcknowledgment}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Application Summary */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
        <Typography variant="body2" color="success.dark">
          <CheckCircle sx={{ verticalAlign: 'middle', mr: 1 }} />
          <strong>Almost Done!</strong> Once you submit your application, our team will review it within 2-3 business days. 
          You'll receive an email with next steps, including background check instructions and training schedule.
        </Typography>
      </Box>
    </Box>
  );
};

export default PaymentLegalStep;